import React from 'react';
import { Sidebar, TopNav, ContentContainer } from '../components/layout';

/**
 * AppLayout
 *
 * Primary authenticated application shell.
 * Renders: TopNav + collapsible Sidebar + scrollable ContentContainer.
 *
 * Usage:
 *   <AppLayout>
 *     <YourPageContent />
 *   </AppLayout>
 *
 * Props:
 *   children       — page content rendered in ContentContainer
 *   sidebarOpen    — controlled sidebar open state (optional, defaults to true)
 *   onSidebarToggle — callback to toggle sidebar (optional)
 */
function AppLayout({
  children,
  sidebarOpen = true,
  onSidebarToggle,
  contentNoPadding = false,
  contentMaxWidth,
  contentScrollable = true,
  contentClassName = '',
}) {
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  const handleToggle = () => {
    if (window.innerWidth < 768) {
      setMobileDrawerOpen(value => !value);
      return;
    }
    onSidebarToggle?.();
  };

  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  return (
    <div className="flex h-screen text-gray-100 overflow-hidden" style={{ background: 'var(--axial-bg)' }}>
      <div className="hidden md:flex">
        <Sidebar open={sidebarOpen} onToggle={handleToggle} />
      </div>

      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <button
            type="button"
            aria-label="Close sidebar"
            onClick={closeMobileDrawer}
            className="absolute inset-0 bg-black/50"
          />
          <div className="relative z-10 h-full">
            <Sidebar open onToggle={closeMobileDrawer} onNavigate={closeMobileDrawer} />
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 min-w-0">
        <TopNav onSidebarToggle={handleToggle} />
        <ContentContainer
          noPadding={contentNoPadding}
          maxWidth={contentMaxWidth}
          scrollable={contentScrollable}
          className={contentClassName}
        >
          {children}
        </ContentContainer>
      </div>
    </div>
  );
}

export default AppLayout;
